import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useFirebaseAuth } from "@/hooks/useFirebaseAuth";
import { setActiveMerchantId } from "@/lib/api";
import { eden } from "@/lib/eden";
import { edenData } from "@/lib/eden-data";
import { queryKeys } from "@/lib/query-keys";
import { Permission } from "@/types/permissions";
import type { UserProfile } from "@/types/profile";

/** Pick the merchant id this user should act as. Prefers an already-resolved
 *  active id (from a previous header), then the first membership, then null. */
function resolveActiveMerchantId(profile: UserProfile): string | null {
  if (profile.merchantId) return profile.merchantId;
  if (profile.activeMerchantId) return profile.activeMerchantId;
  return profile.memberships?.[0]?.merchantId ?? null;
}

export function useProfile() {
  const { isReady, isAuthenticated, testMode, user } = useFirebaseAuth();
  const subject = user?.uid ?? (testMode ? "e2e" : "anonymous");
  const query = useQuery({
    queryKey: queryKeys.session.profile(subject),
    queryFn: async () => edenData(await eden.v1.auth.me.get()),
    enabled: isAuthenticated,
    retry: false,
    staleTime: 60_000,
  });
  const profile: UserProfile | null = query.data ?? null;

  useEffect(() => {
    if (!profile) return;
    // Prime the `X-Merchant-Id` header so permission-gated endpoints resolve
    // their merchant. Loader-based session hydration normally sets it first;
    // this keeps direct component mounts and recovery paths correct too.
    setActiveMerchantId(resolveActiveMerchantId(profile));
  }, [profile]);

  // Guards must stop waiting after either success or failure. When signed out,
  // there is no profile request to wait for.
  // A route loader can restore auth before FirebaseAuthSync's first effect.
  // Keep permission guards waiting until that provider has published the user.
  const profileLoaded = isReady && (!isAuthenticated || !query.isPending);

  const permissions = profile?.permissions ?? [];
  const canManageBilling = permissions.includes(Permission.ManageBilling);
  const canInviteAdmin = permissions.includes(Permission.InviteAdmin);
  const canManageAiBot = permissions.includes(Permission.ManageAiBot);
  const canConnectPlatform = permissions.includes(Permission.ConnectPlatform);
  const canManageOrders = permissions.includes(Permission.ManageOrders);

  return {
    profile,
    profileLoaded,
    permissions,
    canManageBilling,
    canInviteAdmin,
    canManageAiBot,
    canConnectPlatform,
    canManageOrders,
  };
}
